import { Search } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { CourierPickupStatusGroup } from '@/api/types/pickups';
import { DatePicker } from '@/components/ui/date-picker';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { FILTER_ANY_VALUE } from '@/lib/filters';

export type CourierPickupFilterValues = {
  pickup_location: string;
  date_created: string;
  date_collected: string;
  status: CourierPickupStatusGroup | '';
};

export const EMPTY_PICKUP_FILTERS: CourierPickupFilterValues = {
  pickup_location: '',
  date_created: '',
  date_collected: '',
  status: '',
};

const STATUS_GROUPS: CourierPickupStatusGroup[] = [
  'created',
  'pending',
  'collected',
  'cancelled',
  'exception',
];

const fieldLabelClasses = 'text-sm font-medium leading-none text-black';
const inputClasses =
  'h-9 w-full rounded-[10px] border border-[#e5e5e5] bg-white px-3 text-sm focus-visible:outline-none placeholder:text-[#737373]';

export function CourierPickupsFilters({
  values,
  onApply,
}: {
  values: CourierPickupFilterValues;
  onApply: (values: CourierPickupFilterValues) => void;
}) {
  const { t } = useTranslation('pickup-orders');
  const [draft, setDraft] = useState<CourierPickupFilterValues>(values);

  const set = <K extends keyof CourierPickupFilterValues>(
    key: K,
    value: CourierPickupFilterValues[K],
  ) => setDraft((prev) => ({ ...prev, [key]: value }));

  const apply = () => onApply(draft);

  return (
    <div className="flex flex-wrap items-end gap-4">
      <div className="flex w-[220px] flex-col gap-3">
        <span className={fieldLabelClasses}>{t('filters.pickupPoint')}</span>
        <input
          value={draft.pickup_location}
          onChange={(e) => set('pickup_location', e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && apply()}
          placeholder={t('filters.pickupPointPlaceholder')}
          className={inputClasses}
        />
      </div>

      <div className="flex w-[170px] flex-col gap-3">
        <span className={fieldLabelClasses}>{t('filters.dateCreated')}</span>
        <DatePicker
          value={draft.date_created || undefined}
          onChange={(v) => set('date_created', v ?? '')}
          placeholder={t('filters.pickDate')}
          aria-label={t('filters.dateCreated')}
        />
      </div>

      <div className="flex w-[170px] flex-col gap-3">
        <span className={fieldLabelClasses}>{t('filters.dateCollected')}</span>
        <DatePicker
          value={draft.date_collected || undefined}
          onChange={(v) => set('date_collected', v ?? '')}
          placeholder={t('filters.pickDate')}
          aria-label={t('filters.dateCollected')}
        />
      </div>

      <div className="flex w-[170px] flex-col gap-3">
        <span className={fieldLabelClasses}>{t('filters.status')}</span>
        <Select
          value={draft.status || FILTER_ANY_VALUE}
          onValueChange={(v) =>
            set('status', v === FILTER_ANY_VALUE ? '' : (v as CourierPickupStatusGroup))
          }
        >
          <SelectTrigger className="h-9 rounded-[10px] border-[#e5e5e5] bg-white px-3 shadow-none focus:ring-0 data-[placeholder]:text-[#737373]">
            <SelectValue placeholder={t('filters.anyStatus')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={FILTER_ANY_VALUE}>{t('filters.anyStatus')}</SelectItem>
            {STATUS_GROUPS.map((group) => (
              <SelectItem key={group} value={group}>
                {t(`statusGroups.${group}`)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <button
        type="button"
        onClick={apply}
        className="flex h-9 items-center justify-center gap-2 rounded-[10px] border border-black bg-white px-4 text-sm font-medium leading-5 text-black hover:bg-accent"
      >
        <Search className="size-4" />
        {t('filters.search')}
      </button>
    </div>
  );
}
